import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import {
  BookabilityDecision,
  type BookabilityStatusItem,
  type BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction";
import { useCallback, useEffect, useState } from "react";

import { CARDS } from "./BookabilityCard";

interface RenderDecision {
  willRender: boolean;
  unmetBookabilityCriteria: BookabilityUnmetCriteria | undefined;
}

function getUnmetCriteriaWithoutCards(
  renderedUnmetCriteria: BookabilityUnmetCriteria[],
  bookabilityStatus: BookabilityStatusItem,
  ignoreUnmetCriteria?: BookabilityUnmetCriteria[]
): string[] {
  if (bookabilityStatus.attributes.bookability.decision !== BookabilityDecision.BLOCKED) {
    return [];
  }

  const cardsCriteria = new Set(
    renderedUnmetCriteria.map((criteria) => criteria.toString()).filter(isDefined)
  );

  return bookabilityStatus.attributes.bookability.unmetCriteria
    .filter(
      (criteria) =>
        !cardsCriteria.has(criteria.toString()) && !ignoreUnmetCriteria?.includes(criteria)
    )
    .map((criteria) => criteria.toString());
}

interface UseBookabilityCardsStateProps {
  bookabilityStatus: BookabilityStatusItem;
  ignoreUnmetCriteria?: BookabilityUnmetCriteria[];
}

export function useRenderedBookabilityCardTracking({
  bookabilityStatus,
  ignoreUnmetCriteria,
}: UseBookabilityCardsStateProps) {
  const [willRenderValues, setWillRenderValues] = useState<Record<string, RenderDecision>>({});
  const [decisionsReceived, setDecisionsReceived] = useState<Set<string>>(new Set());

  const handleRenderDecision = useCallback(
    (
      cardId: string,
      willRender: boolean,
      unmetBookabilityCriteria: BookabilityUnmetCriteria | undefined
    ) => {
      setWillRenderValues((previous) => ({
        ...previous,
        [cardId]: {
          willRender,
          unmetBookabilityCriteria,
        },
      }));
      setDecisionsReceived((previous) => new Set(previous).add(cardId));
    },
    []
  );

  useEffect(() => {
    if (decisionsReceived.size === CARDS.length) {
      const renderedCriteria = Object.values(willRenderValues)
        .map((value) => value.unmetBookabilityCriteria)
        .filter(isDefined);

      const unmetCriteriaWithoutCards = getUnmetCriteriaWithoutCards(
        renderedCriteria,
        bookabilityStatus,
        ignoreUnmetCriteria
      );

      if (unmetCriteriaWithoutCards.length > 0) {
        logEvent(APP_V2_USER_EVENTS.UNMET_CRITERIA_WITHOUT_A_CARD, {
          criteria: unmetCriteriaWithoutCards,
        });
      }
    }
  }, [bookabilityStatus, decisionsReceived, willRenderValues, ignoreUnmetCriteria]);

  return {
    handleRenderDecision,
  };
}
